import { LocalDate } from "@js-joda/core";
import { AxiosResponse } from "axios";
import { axiosRequest } from "../api.utils";
import { API_ROOT } from "services/baseURLs.config";
import { CompanyGig, CompanyGigShift, GigCosts, GigRating } from "model/Gig";
import { GigContract } from "model/GigContract";

export interface UpdateGigSkillPayload {
  id?: number;
  skill: string;
  experience: number;
}

export interface UpdateGigShiftPayload {
  id?: number;
  startTime: string;
  endTime: string;
  breakStartTime?: string | null;
  breakEndTime?: string | null;
  deductionAmount?: number;
}

export interface UpdateGigPayload {
  paymentType: string;
  role: string;
  description: string;
  minRate: number;
  maxRate: number;
  amountOfWorkers: number;
  workGroupId: number;
  shifts: UpdateGigShiftPayload[];
  skills: UpdateGigSkillPayload[];
}
export interface CopySchedulePayload {
  copyFromDates: LocalDate[];
  createInDates: LocalDate[];
  workerId: number;
}

interface CopyScheduleRequestPayload {
  fromStart: string;
  fromEnd: string;
  toStart: string;
  toEnd: string;
  workerId: number;
}

const buildCopyScheduleRequestPayload = (
  data: CopySchedulePayload,
): CopyScheduleRequestPayload | null => {
  const fromStartDate = data.copyFromDates[0];
  const fromEndDate = data.copyFromDates[data.copyFromDates.length - 1];
  const toStartDate = data.createInDates[0];
  const toEndDate = data.createInDates[data.createInDates.length - 1];

  if (!fromStartDate || !fromEndDate || !toStartDate || !toEndDate) {
    return null;
  }

  return {
    workerId: data.workerId,
    fromStart: fromStartDate.toString(),
    fromEnd: fromEndDate.toString(),
    toStart: toStartDate.toString(),
    toEnd: toEndDate.toString(),
  };
};

const getGigsWithState = async (
  companyId: number,
  state: string,
  sortAscending: boolean,
  page: number,
  amount: number,
  search: string | null,
  workGroupId: number | null,
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth
    .get<any>(`${API_ROOT}/companies/${companyId}/gigs/v2`, {
      params: {
        state,
        sortAscending,
        page,
        amount,
        search,
        workGroupId,
      },
    })
    .then((res) => res);
};

const getActiveGigsCount = async (
  companyId: number,
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth
    .get<any>(`${API_ROOT}/companies/${companyId}/activegigs`)
    .then((res) => res);
};

const getGig = async (
  companyId: number,
  gigId: number,
): Promise<AxiosResponse<CompanyGig>> => {
  return await axiosRequest.auth
    .get<CompanyGig>(`${API_ROOT}/companies/${companyId}/gigs/${gigId}`)
    .then((res) => res);
};

const getGigContract = (
  companyId: number,
  applicationId: number,
): Promise<AxiosResponse<GigContract>> => {
  return axiosRequest.auth
    .get<GigContract>(
      `${API_ROOT}/companies/${companyId}/applications/${applicationId}/contract`,
    )
    .then((res) => res);
};

const getGigRating = (
  companyId: number,
  gigId: number,
): Promise<AxiosResponse<GigRating[]>> => {
  return axiosRequest.auth
    .get<GigRating[]>(`${API_ROOT}/companies/${companyId}/gigrating/${gigId}`)
    .then((res) => res);
};

const getGigCost = (
  companyId: number,
  data: any,
): Promise<AxiosResponse<GigCosts>> => {
  return axiosRequest.auth
    .post<any>(`${API_ROOT}/companies/${companyId}/gigs/costs`, data)
    .then((res) => res.data)
    .catch((err) => console.log(err));
};

const getNonWorkDays = async (
  companyId: number,
  year: number,
  includeWeekends: boolean,
): Promise<AxiosResponse<string[]>> => {
  return await axiosRequest.auth.get<string[]>(
    `${API_ROOT}/companies/${companyId}/nonWorkDays?year=${year}&includeWeekends=${includeWeekends}`,
  );
};

const getOverlappingGig = async (
  companyId: number,
  data: any,
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.post(
    `${API_ROOT}/companies/${companyId}/gigs/v2/overlap`,

    data,
  );
};

const createParentGig = async (
  companyId: number,
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.post(
    `${API_ROOT}/companies/${companyId}/gigs/v2/container`,
  );
};

const createGig = async (
  companyId: number,
  data: any,
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.post(
    `${API_ROOT}/companies/${companyId}/gigs/v2`,

    data,
  );
};

const updateGig = async (
  companyId: number,
  data: UpdateGigPayload,
  gigId: number,
): Promise<AxiosResponse<CompanyGig>> => {
  return await axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/gigs/${gigId}/v2`,
    data,
  );
};

const closeGig = async (
  companyId: number,
  data: any,
  gigId: number,
): Promise<AxiosResponse<any>> => {
  return await axiosRequest.auth.put(
    `${API_ROOT}/companies/${companyId}/gigs/${gigId}/close`,
    data,
  );
};

const copySchedule = async (
  companyId: number,
  data: CopySchedulePayload,
): Promise<AxiosResponse<CompanyGigShift[]>> => {
  const requestPayload = buildCopyScheduleRequestPayload(data);
  if (requestPayload == null) {
    throw new Error("Invalid copy schedule payload");
  }

  return await axiosRequest.auth.post<CompanyGigShift[]>(
    `${API_ROOT}/companies/${companyId}/gigs/copy`,
    requestPayload,
  );
};

export const gig = {
  getGigsWithState,
  getGig,
  getGigContract,
  getActiveGigsCount,
  getGigRating,
  getGigCost,
  getNonWorkDays,
  updateGig,
  createGig,
  createParentGig,
  getOverlappingGig,
  closeGig,
  copySchedule,
};
